import { useEffect, useRef, type ReactNode } from "react";
import { JanelaProvider } from "@/components/JanelaContexto";

/** Extrai o nome do arquivo quando o ambiente disponibiliza import.meta.url. */
export function nomeArquivoDoModulo(url?: string) {
  if (!url) return "";
  return url.split("/").pop()?.split("?")[0] ?? "";
}

/** Rodapé padrão das janelas: mostra o nome da aba/rota, o arquivo .tsx e @@@. */
export function RodapeJanela({ nome, arquivo }: { nome: string; arquivo?: string | undefined }) {
  const exibicao = [nome, arquivo ?? "", "@@@"].filter(Boolean).join(" | ");
  return (
    <div className="mt-auto shrink-0 border-t border-border bg-muted/40 px-[10px] py-1 text-xs text-muted-foreground">
      {exibicao}
    </div>
  );
}

/** Pilha das janelas abertas, para que o ESC feche apenas a do topo. */
const pilhaJanelas: Array<() => void> = [];

/** Trilha de títulos/arquivos das janelas abertas, do fundo ao topo (depuração). */
export type NoTrilha = { titulo: string; rota?: string; arquivo?: string };
const trilhaJanelas: NoTrilha[] = [];

/** Sequência de janelas abertas (da raiz até a do topo). Cópia imutável. */
export function trilhaDeJanelas(): NoTrilha[] {
  return trilhaJanelas.slice();
}

/** Quantidade de janelas cheias abertas no momento. */
export function janelasAbertas() {
  return pilhaJanelas.length;
}
let ouvinteEsc = false;

function instalarEsc() {
  if (ouvinteEsc) return;
  ouvinteEsc = true;
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    // Diálogos, popovers, selects e comboboxes abertos têm prioridade no ESC.
    const sobreposto =
      '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper], [data-state="open"][role="listbox"], [cmdk-list]';
    if (document.querySelector(sobreposto)) return;

    const topo = pilhaJanelas.at(-1);
    if (topo) {
      // Impede que outros ouvintes (ex.: a janela de página do menu) também
      // reajam ao mesmo ESC e fechem um nível a mais.
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      topo();
    }
  });
}

/**
 * Painel que ocupa todo o espaço abaixo do header, sem bordas, sem margens
 * e sem sobreposição escura — o mesmo formato das janelas abertas pelo menu.
 */
export function JanelaCheia({
  aberta,
  aoFechar,
  titulo,
  rota,
  arquivo,
  herdarJanela = false,
  children,
}: {
  aberta: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  rota?: string;
  arquivo?: string;
  herdarJanela?: boolean;
  children: ReactNode;
}) {
  const fecharRef = useRef(aoFechar);
  fecharRef.current = aoFechar;

  useEffect(() => {
    if (!aberta || herdarJanela) return;
    instalarEsc();
    const fechar = () => fecharRef.current();
    pilhaJanelas.push(fechar);
    const no: NoTrilha = { titulo, rota, arquivo };
    trilhaJanelas.push(no);
    return () => {
      const i = pilhaJanelas.lastIndexOf(fechar);
      if (i >= 0) pilhaJanelas.splice(i, 1);
      const j = trilhaJanelas.lastIndexOf(no);
      if (j >= 0) trilhaJanelas.splice(j, 1);
    };
  }, [aberta, herdarJanela, titulo, rota, arquivo]);

  if (!aberta) return null;
  if (herdarJanela) return <>{children}</>;

  const nivel = pilhaJanelas.length;

  return (
    <div
      data-janela-cheia
      aria-label={titulo}
      className="fixed left-0 right-0 bottom-0 top-[var(--header-h,64px)] flex flex-col bg-background"
      style={{ zIndex: 50 + nivel }}
    >
      <div className="w-full flex-1 overflow-y-auto border-0 bg-background px-[10px] pt-0 pb-0">
        <JanelaProvider aoFechar={aoFechar} arquivo={arquivo} titulo={titulo}>
          {children}
        </JanelaProvider>
      </div>
    </div>
  );
}
